import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import AlignBoxMiddleCenter16 from '@carbon/icons-react/es/align-box--middle-center/16';
import ChevronDown20 from '@carbon/icons-react/es/chevron--down/20';
import Close16 from '@carbon/icons-react/es/close/16';
import Close20 from '@carbon/icons-react/es/close/20';
import { List, Space, Tooltip, Typography } from 'antd';
import React, { memo } from 'react';
import { FormattedMessage } from 'react-intl';
import { components, defaultTheme } from 'react-select';
import styled, { css } from 'styled-components';
import { ifProp } from 'styled-tools';
import HolidayCalendarIconSVG from 'assets/svgs/holiday-calendar-display.svg';
import TimeOffBoat from 'assets/svgs/timeoff-sailboat.svg';
import { CurrencyFlagSizes } from 'components/atoms/currency/types';
import { HeightStyle } from 'components/atoms/input/input';
import { BodyShort01, SingleLineEllipsis } from 'components/styles';
import { useHolidayCalendar } from 'hooks/use-holiday-calendars';
import { useFlexUserOptionAvailability } from 'hooks/use-flex-user-option-availability';
import { getCurrencyLabel } from 'utils/currency';
import { getHolidayArray } from 'utils/holiday-calendar-utils';
import { getTimeOffArray } from 'utils/time-off-utils';
import { GetHolidayTooltipContent } from '../../molecules/holiday-tooltip';
import { Avatar } from '../avatar';
import { CurrencyFlagRenderer } from '../currency-renderer';
import { IconWrapper } from '../icon-shell/icon-shell';
import { Tag as TagAtom } from '../tag';
import { GetTimeOffTooltipContent } from '../time-off-tooltip';
import { FlexUserAvatar, FlexUserOption } from './flex-option-layout';
export const StyledDropdownIndicator = styled(components.DropdownIndicator).attrs({
    theme: {
        ...defaultTheme
    }
})``;
export const StyledClearIndicator = styled(components.ClearIndicator).attrs({
    theme: {
        ...defaultTheme
    }
})``;
export const StyledTagOption = styled(components.Option).attrs({
    theme: {
        ...defaultTheme
    }
})`
  :root[data-new-theme-enabled='true'] & {
    min-height: var(--height-base);
    height: auto;
    padding-top: var(--padding-xs);
    padding-bottom: var(--padding-xs);
    width: 100%;
  }
`;
export const StyledSingleValue = styled(components.SingleValue).attrs({
    theme: {
        ...defaultTheme
    }
})`
  :root[data-new-theme-enabled='true'] & {
    padding-left: var(--padding-xs);
    padding-right: var(--padding-xs);
    padding-top: 0 !important;
    padding-bottom: 0 !important;
    min-height: var(--height-sm);
    display: flex;
    align-items: center;
  }
`;
export const StyledMultiValueRemove = styled(components.MultiValueRemove).attrs({
    theme: {
        ...defaultTheme
    }
})``;
export const StyledMultiValueLabel = styled(components.MultiValueLabel).attrs({
    theme: {
        ...defaultTheme
    }
})``;
export const StyledMultiValue = styled(components.MultiValue).attrs({
    theme: {
        ...defaultTheme
    }
})`
  :root[data-new-theme-enabled='true'] & {
    padding-left: var(--padding-xs);
    padding-right: var(--padding-xs);
    padding-top: 0 !important;
    padding-bottom: 0 !important;
    min-height: var(--height-base);
    display: flex;
    align-items: center;
  }
`;
const AdditionalInfo = styled.div`
  ${BodyShort01};
  color: var(--gray-50);
`;
const CurrencyOptionLabel = styled.div`
  display: flex;
  align-items: center;
  gap: var(--spacing-03);
  flex: 1;
`;
const CurrencyOptionWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: var(--spacing-03);
`;
const Tag = styled(TagAtom)`
  padding: 0 var(--spacing-02);
`;
const Wrapper = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
`;
const StyleAvatar = styled(Avatar)`
  margin-right: var(--spacing-03);
  flex-shrink: 0;
`;
export const TimeOffWrapper = styled(Wrapper)`
  gap: var(--spacing-03);
  svg {
    color: var(--purple-60);
  }
`;
export const TextWrapper = styled.div`
  width: 98%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  ${BodyShort01};
  > span {
    color: inherit;
  }
`;
export const AvatarTag = ({ color = 'gray', imageUrl, label, logoUrl, ...restProps })=>/*#__PURE__*/ _jsx(Tag, {
        ...restProps,
        color: color,
        children: /*#__PURE__*/ _jsxs(Wrapper, {
            children: [
                /*#__PURE__*/ _jsx(StyleAvatar, {
                    size: "small",
                    src: imageUrl,
                    companyLogoUrl: logoUrl,
                    name: label
                }),
                /*#__PURE__*/ _jsx(Typography.Text, {
                    ellipsis: true,
                    title: label,
                    children: label
                })
            ]
        })
    });
export const DropdownIndicator = (props)=>{
    return /*#__PURE__*/ _jsx(StyledDropdownIndicator, {
        ...props,
        children: /*#__PURE__*/ _jsx(ChevronDown20, {})
    });
};
export const ClearIndicator = (props)=>/*#__PURE__*/ _jsx(StyledClearIndicator, {
        ...props,
        children: /*#__PURE__*/ _jsx(Close20, {})
    });
const StyledControl = styled(components.Control).attrs({
    theme: {
        ...defaultTheme
    }
})`
  outline-offset: -2px;
  outline: 2px solid !important;
  outline-color: transparent !important;
  border: 0;
  border-bottom: 1px solid var(--gray-50);
  border-radius: 0;
  background-color: var(--gray-10);
  min-height: ${HeightStyle};
  ${ifProp('isFocused', css`
      box-shadow: none;
      border-bottom-color: transparent !important;
      outline-color: var(--primary-color) !important;
      outline-offset: -2px;
    `)};
  &:hover {
    background-color: var(--hover-color);
  }
`;
export const Control = (props)=>/*#__PURE__*/ _jsx(StyledControl, {
        ...props
    });
export const TagOption = (props)=>{
    const { data } = props;
    return /*#__PURE__*/ _jsx(StyledTagOption, {
        ...props,
        children: /*#__PURE__*/ _jsx(TagAtom, {
            color: data.color,
            children: data.label
        })
    });
};
export const TagSingleValue = (props)=>{
    const { data } = props;
    return /*#__PURE__*/ _jsx(StyledSingleValue, {
        ...props,
        children: /*#__PURE__*/ _jsx(TagAtom, {
            color: data.color,
            children: data.label
        })
    });
};
export const MultiValueRemove = (props)=>/*#__PURE__*/ _jsx(StyledMultiValueRemove, {
        ...props,
        children: /*#__PURE__*/ _jsx(Close16, {})
    });
export const MultiValueLabel = (props)=>{
    const { data: data1 } = props;
    const data = data1;
    return /*#__PURE__*/ _jsx(StyledMultiValueLabel, {
        ...props,
        children: /*#__PURE__*/ _jsxs(Space, {
            size: "small",
            children: [
                /*#__PURE__*/ _jsx(Avatar, {
                    size: "small",
                    src: data.imageUrl,
                    name: data.__isNew__ ? data.value.toString() : data.label,
                    companyLogoUrl: data.logoUrl
                }),
                /*#__PURE__*/ _jsx("div", {
                    children: data.label
                })
            ]
        })
    });
};
export const CurrencyValueLabel = (props)=>{
    const { data: data1 } = props;
    const data = data1;
    return /*#__PURE__*/ _jsx(StyledMultiValueLabel, {
        ...props,
        children: /*#__PURE__*/ _jsxs(CurrencyOptionLabel, {
            children: [
                /*#__PURE__*/ _jsx(CurrencyFlagRenderer, {
                    currency: data.value,
                    size: CurrencyFlagSizes.SMALL
                }),
                getCurrencyLabel(data.extra)
            ]
        })
    });
};
export const MultiValue = (props)=>{
    const { data } = props;
    return /*#__PURE__*/ _jsx(StyledMultiValue, {
        ...props,
        children: /*#__PURE__*/ _jsx(Tag, {
            color: (data === null || data === void 0 ? void 0 : data.color) || 'gray'
        })
    });
};
export const ListItem = styled(List.Item)`
  padding: 0 !important;
  cursor: ${(props)=>props.isDisabled ? 'not-allowed' : 'pointer'};
  opacity: ${(props)=>props.isDisabled ? 0.5 : 1};
  :root[data-new-theme-enabled='true'] & {
    padding: 0 !important;
    display: flex;
    align-items: center;
  }
`;
export const ListItemMeta = styled(List.Item.Meta)`
  align-items: center;
  :root[data-new-theme-enabled='true'] & {
    padding-top: 0 !important;
    padding-bottom: 0 !important;
  }
  & .ant-list-item-meta-title {
    margin-bottom: 0;
    ${({ isSelected })=>isSelected ? css`
            color: var(--white-100) !important;
          ` : css`
            color: var(--gray-100);
          `};

    [data-new-theme-enabled='true'] & {
      ${({ isSelected })=>isSelected ? css`
              color: var(--text-primary) !important;
            ` : css`
              color: var(--text-primary);
            `};
    }
  }
  & .ant-list-item-meta-description {
    font-size: var(--font-size-sm);
    ${({ isSelected })=>isSelected ? css`
            color: var(--white-100);
          ` : css`
            color: var(--gray-100);
          `};
    font-weight: normal;
    ${SingleLineEllipsis};

    [data-new-theme-enabled='true'] & {
      ${({ isSelected })=>isSelected ? css`
              color: var(--text-primary);
            ` : css`
              color: var(--text-primary);
            `};
    }
  }
`;
export const TitleWrapper = ({ title, timeOffText, timeOffArray, holidayText, holidayArray })=>{
    const { Text } = Typography;
    return /*#__PURE__*/ _jsxs(TimeOffWrapper, {
        "data-cy": `tag-option-${title}`,
        children: [
            /*#__PURE__*/ _jsx(TextWrapper, {
                children: /*#__PURE__*/ _jsx(Text, {
                    ellipsis: {
                        tooltip: true
                    },
                    children: title
                })
            }),
            !!timeOffArray && (timeOffArray === null || timeOffArray === void 0 ? void 0 : timeOffArray.length) > 0 && /*#__PURE__*/ _jsx(Tooltip, {
                title: timeOffText,
                children: /*#__PURE__*/ _jsx("img", {
                    src: TimeOffBoat,
                    alt: "time-off"
                })
            }),
            !!holidayArray && (holidayArray === null || holidayArray === void 0 ? void 0 : holidayArray.length) > 0 && /*#__PURE__*/ _jsx(Tooltip, {
                title: holidayText,
                children: /*#__PURE__*/ _jsx("img", {
                    src: HolidayCalendarIconSVG,
                    alt: "holiday"
                })
            })
        ]
    });
};
export const AvatarOption = /*#__PURE__*/ memo((props)=>{
    const { data, isSelected, isFocused, isDisabled } = props;
    let timeOffArray = [];
    let holidayArray = [];
    const { holidayCalendar } = useHolidayCalendar(data === null || data === void 0 ? void 0 : data.holidayCalendarId);
    const selectProps = props === null || props === void 0 ? void 0 : props.selectProps;
    if (!!selectProps.startDate && !!selectProps.dueDate && !!data.userTimeOffDetails) {
        timeOffArray = getTimeOffArray(selectProps.startDate, selectProps.dueDate, data.userTimeOffDetails);
    }
    if (!!selectProps.startDate && !!selectProps.dueDate && !!holidayCalendar) {
        const holidays = holidayCalendar === null || holidayCalendar === void 0 ? void 0 : holidayCalendar.holidays;
        holidayArray = getHolidayArray(selectProps.startDate, selectProps.dueDate, holidays);
    }
    const timeOffText = /*#__PURE__*/ _jsx(GetTimeOffTooltipContent, {
        timeOff: timeOffArray,
        userName: data.label
    });
    const holidayText = /*#__PURE__*/ _jsx(GetHolidayTooltipContent, {
        holiday: holidayArray
    });
    return /*#__PURE__*/ _jsx(StyledTagOption, {
        ...props,
        children: /*#__PURE__*/ _jsx(ListItem, {
            "data-cy": "list-item-user",
            isDisabled: isDisabled,
            children: /*#__PURE__*/ _jsx(ListItemMeta, {
                isFocused: isFocused,
                isSelected: isSelected,
                avatar: /*#__PURE__*/ _jsx(Avatar, {
                    name: data.__isNew__ ? data.value.toString() : data.label,
                    src: data.imageUrl,
                    userStatus: data.userStatus,
                    companyLogoUrl: data.logoUrl
                }),
                title: /*#__PURE__*/ _jsx(TitleWrapper, {
                    title: data.label,
                    timeOffText: timeOffText,
                    timeOffArray: timeOffArray,
                    holidayText: holidayText,
                    holidayArray: holidayArray
                }),
                description: data.description
            })
        })
    });
});
AvatarOption.displayName = 'AvatarOption';
/* Rich user option adapter for package Selects. Shared layout, avatar sizing
   and availability derivation live below this wrapper; this adapter only maps
   the legacy top-level option fields into those typed slots. */ export const FlexUserAvailabilityOption = /*#__PURE__*/ memo((props)=>{
    const { data } = props;
    const selectProps = props === null || props === void 0 ? void 0 : props.selectProps;
    const { timeOffArray, holidayArray } = useFlexUserOptionAvailability({
        holidayCalendarId: data.holidayCalendarId,
        timeOffDetails: data.userTimeOffDetails,
        startDate: selectProps.startDate,
        dueDate: selectProps.dueDate
    });
    return /*#__PURE__*/ _jsx(FlexUserOption, {
        optionProps: props,
        avatar: /*#__PURE__*/ _jsx(FlexUserAvatar, {
            data: data
        }),
        title: /*#__PURE__*/ _jsx(TitleWrapper, {
            title: data.label,
            timeOffText: /*#__PURE__*/ _jsx(GetTimeOffTooltipContent, {
                timeOff: timeOffArray,
                userName: data.label
            }),
            timeOffArray: timeOffArray,
            holidayText: /*#__PURE__*/ _jsx(GetHolidayTooltipContent, {
                holiday: holidayArray
            }),
            holidayArray: holidayArray
        }),
        description: data.description
    });
});
FlexUserAvailabilityOption.displayName = 'FlexUserAvailabilityOption';
export const CompanyOption = (props)=>{
    const { data, isSelected, isFocused } = props;
    return /*#__PURE__*/ _jsx(StyledTagOption, {
        ...props,
        children: /*#__PURE__*/ _jsx(ListItem, {
            "data-cy": `select.option.${data.value}`,
            children: /*#__PURE__*/ _jsx(ListItemMeta, {
                isFocused: isFocused,
                isSelected: isSelected,
                avatar: /*#__PURE__*/ _jsx(Avatar, {
                    name: data.label,
                    src: data.extra.companyLogoUrl
                }),
                title: data.label
            })
        })
    });
};
const { Paragraph: AntParagraph } = Typography;
export const SingleLineLabel = styled(AntParagraph).attrs({
    ellipsis: {
        rows: 1,
        tooltip: true
    }
})`
  margin-bottom: 0;
  color: inherit;
`;
const DescriptiveOptionColorStyles = css`
  ${({ isSelected })=>isSelected && css`
      color: var(--white-100) !important;
    `};

  ${({ isDisabled })=>isDisabled && css`
      color: var(--gray-40) !important;
    `};

  [data-new-theme-enabled='true'] & {
    ${({ isSelected })=>isSelected && css`
        color: var(--text-primary);
      `};
  }
`;
const Icon = styled.div``;
const TextWrapStyles = css`
  white-space: pre-wrap;
  word-break: break-word;
  line-height: 1.4;
`;
export const ListItemForDescriptiveOption = styled(ListItem)`
  gap: var(--spacing-05);

  /* Override fixed height for multi-line descriptive options */
  :root[data-new-theme-enabled='true'] & {
    height: auto;
  }

  ${Icon} {
    ${DescriptiveOptionColorStyles};
  }
  .ant-list-item-meta-title {
    ${BodyShort01};
    color: var(--gray-100);
    ${DescriptiveOptionColorStyles};
    ${TextWrapStyles};
  }
  .ant-list-item-meta-description {
    color: var(--gray-70);
    ${({ isSelected })=>isSelected && css`
        color: var(--white-80);
      `};
    ${({ isDisabled })=>isDisabled && css`
        color: var(--gray-30);
      `};
    line-height: var(--line-height-helper-01);
    white-space: normal;

    [data-new-theme-enabled='true'] & {
      ${({ isSelected })=>isSelected ? css`
              color: var(--text-primary);
            ` : css`
              color: var(--text-primary);
            `};
    }
  }
`;
export const ListItemMetaForDescriptiveOption = styled(ListItemMeta)`
  .ant-list-item-meta-title {
    ${BodyShort01};
    color: var(--gray-100);
    ${DescriptiveOptionColorStyles};
    ${TextWrapStyles};
  }
  .ant-list-item-meta-description {
    color: var(--gray-70);
    ${({ isSelected })=>isSelected && css`
        color: var(--white-80);
      `};
    ${({ isDisabled })=>isDisabled && css`
        color: var(--gray-30);
      `};
    line-height: var(--line-height-helper-01);
    white-space: normal;
  }

  [data-new-theme-enabled='true'] & {
    ${({ isSelected })=>isSelected ? css`
            color: var(--text-primary);
          ` : css`
            color: var(--text-primary);
          `};
  }
`;
export const DescriptiveOption = /*#__PURE__*/ memo((props)=>{
    const { data, isSelected, isFocused } = props;
    return /*#__PURE__*/ _jsx(StyledTagOption, {
        ...props,
        children: /*#__PURE__*/ _jsx(Tooltip, {
            title: data.isDisabled && data.tooltipTextId && /*#__PURE__*/ _jsx(FormattedMessage, {
                id: data.tooltipTextId
            }),
            placement: "right",
            children: /*#__PURE__*/ _jsxs(ListItemForDescriptiveOption, {
                "data-cy": `select.option.${data.value}`,
                isSelected: isSelected,
                isDisabled: data.isDisabled,
                children: [
                    data.icon != null ? /*#__PURE__*/ _jsx(Icon, {
                        children: data.icon
                    }) : null,
                    /*#__PURE__*/ _jsx(ListItemMeta, {
                        isFocused: isFocused,
                        isSelected: isSelected,
                        title: data.label,
                        description: data.description
                    })
                ]
            })
        })
    });
});
DescriptiveOption.displayName = 'DescriptiveOption';
export const SingleLineOption = /*#__PURE__*/ memo((props)=>{
    const { data, isSelected, isFocused } = props;
    return /*#__PURE__*/ _jsx(StyledTagOption, {
        ...props,
        children: /*#__PURE__*/ _jsx(ListItem, {
            children: /*#__PURE__*/ _jsx(ListItemMeta, {
                isFocused: isFocused,
                isSelected: isSelected,
                avatar: /*#__PURE__*/ _jsx(Avatar, {
                    name: data.__isNew__ ? data.value.toString() : data.label,
                    src: data.imageUrl,
                    companyLogoUrl: data.logoUrl
                }),
                title: /*#__PURE__*/ _jsx(SingleLineLabel, {
                    children: data.label
                }),
                description: data.description
            })
        })
    });
});
SingleLineOption.displayName = 'SingleLineOption';
export const SingleValue = (props)=>{
    const { data } = props;
    return /*#__PURE__*/ _jsx(StyledSingleValue, {
        ...props,
        children: /*#__PURE__*/ _jsx(AvatarTag, {
            logoUrl: data.logoUrl,
            imageUrl: data.imageUrl,
            label: data.__isNew__ ? data.value.toString() : data.label
        })
    });
};
export const DescriptiveValue = /*#__PURE__*/ memo((props)=>{
    const { data, isDisabled } = props;
    return /*#__PURE__*/ _jsx(StyledSingleValue, {
        ...props,
        children: /*#__PURE__*/ _jsx(ListItem, {
            "data-cy": `select.option.${data.value}`,
            isDisabled: isDisabled,
            children: /*#__PURE__*/ _jsx(ListItemMetaForDescriptiveOption, {
                isFocused: false,
                isSelected: false,
                isDisabled: data.isDisabled,
                title: /*#__PURE__*/ _jsx(SingleLineLabel, {
                    children: data.label
                }),
                description: data.description
            })
        })
    });
});
DescriptiveValue.displayName = 'DescriptiveValue';
export const CurrencyOption = (props)=>{
    var _data_value;
    const { data, isSelected, isFocused } = props;
    return /*#__PURE__*/ _jsx(StyledTagOption, {
        ...props,
        children: /*#__PURE__*/ _jsx(ListItem, {
            "data-cy": `select.option.${data === null || data === void 0 ? void 0 : data.value}`,
            children: /*#__PURE__*/ _jsx(ListItemMeta, {
                isFocused: isFocused,
                isSelected: isSelected,
                title: /*#__PURE__*/ _jsxs(CurrencyOptionWrapper, {
                    children: [
                        /*#__PURE__*/ _jsxs(CurrencyOptionLabel, {
                            children: [
                                /*#__PURE__*/ _jsx(CurrencyFlagRenderer, {
                                    currency: data === null || data === void 0 ? void 0 : (_data_value = data.value) === null || _data_value === void 0 ? void 0 : _data_value.toString(),
                                    size: CurrencyFlagSizes.SMALL
                                }),
                                getCurrencyLabel(data.extra, false)
                            ]
                        }),
                        /*#__PURE__*/ _jsxs(AdditionalInfo, {
                            children: [
                                "(",
                                data.extra.currencySymbol,
                                ")"
                            ]
                        })
                    ]
                })
            })
        })
    });
};
export const FormOption = (props)=>{
    const { data, isSelected, isFocused } = props;
    return /*#__PURE__*/ _jsx(StyledTagOption, {
        ...props,
        children: /*#__PURE__*/ _jsx(ListItem, {
            children: /*#__PURE__*/ _jsx(ListItemMeta, {
                isFocused: isFocused,
                isSelected: isSelected,
                avatar: /*#__PURE__*/ _jsx(IconWrapper, {
                    children: /*#__PURE__*/ _jsx(AlignBoxMiddleCenter16, {})
                }),
                title: /*#__PURE__*/ _jsx(Tooltip, {
                    title: data.label,
                    children: /*#__PURE__*/ _jsx(Typography.Text, {
                        ellipsis: true,
                        children: data.label
                    })
                })
            })
        })
    });
};
